import { NgModule } from '@angular/core';
import { provideRouter } from '@angular/router';
import { EffectsModule } from '@ngrx/effects';
import { RouterState, StoreRouterConnectingModule } from '@ngrx/router-store';
import { Action, ActionReducer, StoreModule } from '@ngrx/store';
import { StoreDevtoolsModule } from '@ngrx/store-devtools';
import {
    addBreadcrumb,
    browserTracingIntegration,
    captureConsoleIntegration,
    init,
    replayIntegration
} from '@sentry/angular';
import { clearStateMetaReducer } from 'app/app.actions';
import { AppEffects } from 'app/core/app.effects';
import { MapEffects } from 'app/core/map/map.effects';
import { NotificationsEffects } from 'app/core/notifications/notifications.effects';
import { EventsService } from 'app/shared/service/events.service';
import { SimpleNotificationsModule } from 'app/shared/toast';
import { environment } from 'environments/environment';
import { versions } from 'environments/versions';
import { localStorageSync } from 'ngrx-store-localstorage';
import { reducers } from './app.reducer';
import { AuthEffects } from './core/auth/auth.effects';
import { routes } from './routes';
import { BackendModule } from './shared/backend';
import { GoogleModule } from './shared/google';
import { LMapModule } from './shared/ll';
import { LoadingBarModule } from './shared/loading-bar';


if (environment.production) {
    init({
        integrations: [
            browserTracingIntegration(),
            replayIntegration({
                maskAllText: false,
            }),
            captureConsoleIntegration({
                levels: ['error'],
            }),
        ],
        dsn: environment.sentryEndpoint,
        release: versions.revision,
        environment: environment.sentryEnv,
        ignoreErrors: [
            'EvalError',
            'AbortError: The user aborted a request.',
            'AbortError: signal is aborted without reason',
        ],
        denyUrls: [
            'intercomcdn.com',
            'intercom.io',
            'upscope.js',
            'maps.googleapis.com',
            'js.getuserflow.com',
        ],
        tracesSampleRate: 0.01,  // send 1% "transaction" events to enable performance data
        replaysSessionSampleRate: 0.0,
        replaysOnErrorSampleRate: 0.5,
    });
}


export function logActionMiddleware<T>(reducer: ActionReducer<T>): ActionReducer<T, Action> {
    return (state, action) => {
        addBreadcrumb({
            category: 'store.action',
            message: action.type,
            data: action,
        });
        return reducer(state, action);
    };
}

export function localStorageSyncReducer<T>(reducer: ActionReducer<T>): ActionReducer<T, Action> {
    return localStorageSync({ keys: ['properties'], rehydrate: true, restoreDates: false })(reducer);
}

export const metaReducers = environment.production ? [logActionMiddleware] : [localStorageSyncReducer];

@NgModule({
    providers: [
        provideRouter(routes),
    ],
    imports: [
        StoreModule.forRoot(reducers, {
            metaReducers: [clearStateMetaReducer, ...metaReducers],
            runtimeChecks: {
                strictStateImmutability: !environment.production,
                strictActionImmutability: !environment.production,
                strictStateSerializability: !environment.production,
                strictActionSerializability: !environment.production,
                strictActionTypeUniqueness: !environment.production,
            },
        }),
        StoreRouterConnectingModule.forRoot({ routerState: RouterState.Minimal }),
        EffectsModule.forRoot([AppEffects, AuthEffects, MapEffects, NotificationsEffects, EventsService]),
        LMapModule.forRoot({ mapboxApiKey: environment.mapboxApiKey }),
        BackendModule.forRoot({ apiEndpoint: environment.apiEndpoint, wsEndpoint: environment.wsEndpoint }),
        GoogleModule.forRoot({ googleApiKey: environment.googleApiKey }),
        SimpleNotificationsModule.forRoot(),
        LoadingBarModule.forRoot(),
        ...(environment.production ? [] : [StoreDevtoolsModule.instrument()]),
    ],
})
export class AppModule {}
